import React, { forwardRef, useImperativeHandle, useMemo } from "react";
import { Typography, Table, Col } from "antd";
import DpatSubIndicatorSection from "../setup/DpatSubIndicatorSection";
import DpatIndicatorCmsIntro from "../setup/DpatIndicatorCmsIntro";
import Comment from "components/Comments";
import CiResultDisplay from "./CiResultDisplay";
import { resolveMeetingDataProp } from "utils/dpatAssessmentStartData";
import { renderDpatEvidenceLink } from "utils/renderDpatEvidenceLink";

const { Title, Text } = Typography;

const MEETING_LINK_KEYS = ["docs", "invitation", "recommendation"];

const LINK_LABELS = {
  docs: "View Minutes",
  invitation: "View Invitation",
  recommendation: "View Document",
};

function formatOptionalNumber(value) {
  if (value == null || value === "" || value === "N/A" || Number.isNaN(value)) {
    return "";
  }
  return value;
}

const ManagementMeeting = forwardRef(({
  data,
  year,
  columns,
  districtId,
  hideComment,
  savedAssessmentData,
  assessmentStartLoading,
  isAssessmentStarted,
}, ref) => {
  const displayData = resolveMeetingDataProp({
    isAssessmentStarted,
    savedAssessmentData,
    liveData: data,
  });

  const meetingRows = displayData?.data ?? [];

  const meetingTableColumns = useMemo(
    () => (columns ?? []).map((col) => {
      if (col.dataIndex === "hodAttendance" || col.dataIndex === "attendance") {
        return {
          ...col,
          render: (value) => formatOptionalNumber(value),
        };
      }

      if (MEETING_LINK_KEYS.includes(col.dataIndex)) {
        return {
          ...col,
          render: (value) => renderDpatEvidenceLink(
            value,
            LINK_LABELS[col.dataIndex] ?? "View Document",
          ),
        };
      }

      return col;
    }),
    [columns],
  );

  useImperativeHandle(ref, () => ({
    getData: () => ({
      indicator: "CI2",
      area: "Other Statutory Meetings / Requirements",
      data,
    }),
  }));

  const DPAT_SLUG = "c2.0-2.3";

  return (
    <Comment
      data={data}
      year={year}
      districtId={districtId}
      tableCommentedId={`c2.0-2.3-${year}`}
      hideComment={hideComment}
    >
      {({ renderCommentFeedbackFooter }) => (
        <DpatSubIndicatorSection code={DPAT_SLUG}>
          <DpatIndicatorCmsIntro
            year={year}
            slug={DPAT_SLUG}
          />

          <Col align="start">
            <CiResultDisplay slug={DPAT_SLUG} fulfillment={displayData?.fulfillment} />
          </Col>

          {assessmentStartLoading && <Text>Loading saved assessment data…</Text>}

          <Title level={4} style={{ marginTop: "20px" }}>Evidence of Management Meetings</Title>

          {meetingRows.length > 0 ? (
            <Table
              columns={meetingTableColumns}
              dataSource={meetingRows}
              pagination={false}
              bordered
              rowKey={(record, index) => record.key || index}
            />
          ) : (
            <Text>No management meetings data available</Text>
          )}
          {!hideComment && renderCommentFeedbackFooter()}
        </DpatSubIndicatorSection>
      )}
    </Comment>
  );
});

export default ManagementMeeting;
